/**
 * GA4 event tracking for Boost widget events that do NOT go through the
 * standard `sendEvent()` click flow (filter clicks, search queries, suggestion
 * term clicks, notify-me, and widget view).
 *
 * Product-click / add-to-cart / buy-now / pre-order events are handled by
 * attaching `ga4_info` to the `sendEvent()` payload in `onClickTrackingEvent`
 * (see `ga4-integration.js`). The Lambda reads `ga4_info` and forwards all
 * events to GA4 Measurement Protocol server-side.
 */

import { sendGa4EventToLambda } from '../@deprecation/utils/ga4-integration';
import { GA4_BOOST_EVENT } from './constants';

// ─── Per-event deduplication ─────────────────────────────────────────────────

/** Deduplicates `boost_search_query` — fires once per unique query per page load. @type {Set<string>} */
const _sentSearchQueries = new Set();

// ─── Public event senders ────────────────────────────────────────────────────

/**
 * `boost_search_query`
 *
 * Fires when a user submits a search query from the search box or clicks
 * "View all results" in the Instant Search Widget (ISW).
 *
 * Deduplicated per page load: the same query string only sends once.
 *
 * @param {{ searchQuery: string }} params
 */
export async function sendGa4SearchQueryEvent({ searchQuery }) {
  if (!searchQuery || _sentSearchQueries.has(searchQuery)) return;
  _sentSearchQueries.add(searchQuery);

  await sendGa4EventToLambda(GA4_BOOST_EVENT.SEARCH_QUERY, { qs: searchQuery });
}

/**
 * `boost_suggestion_term_click`
 *
 * Fires when a user clicks a suggestion term in the "Did you mean" widget
 * inside the Instant Search Widget.
 *
 * @param {{ suggestionTerm: string, searchQuery?: string }} params
 */
export async function sendGa4SuggestionTermClickEvent({ suggestionTerm, searchQuery }) {
  await sendGa4EventToLambda(GA4_BOOST_EVENT.SUGGESTION_TERM_CLICK, {
    suggestion_term: suggestionTerm || '',
    qs: searchQuery || '',
  });
}

/**
 * `boost_filter_option_value_click`
 *
 * Fires when a user clicks on a filter option value in the filter tree.
 *
 * @param {{ searchQuery?: string, filterOptionValue: string }} params
 */
export async function sendGa4FilterOptionValueClickEvent({ searchQuery, filterOptionValue }) {
  await sendGa4EventToLambda(GA4_BOOST_EVENT.FILTER_OPTION_VALUE_CLICK, {
    filter_option_value: filterOptionValue || '',
    qs: searchQuery || '',
  });
}

/**
 * `boost_suggestion_term_isw_click`
 *
 * Fires when a user clicks a suggestion term in the ISW suggestion list
 * (the autocomplete suggestions, NOT the "Did you mean" / queries section).
 * Targets `.boost-sd__suggestion-queries-item--suggestion` items.
 *
 * @param {{ suggestionTerm: string, searchQuery?: string }} params
 */
export async function sendGa4SuggestionTermIswClickEvent({ suggestionTerm, searchQuery }) {
  await sendGa4EventToLambda(GA4_BOOST_EVENT.SUGGESTION_TERM_ISW_CLICK, {
    suggestion_term: suggestionTerm || '',
    qs: searchQuery || '',
  });
}

/**
 * `boost_search_result_isw_click`
 *
 * Fires when a user clicks a non-product search result in the ISW
 * (collections, pages, or blog articles). Uses `a.boost-sd__suggestion-queries-item-link`
 * to identify the click; `result_type` is inferred from the link href.
 *
 * @param {{ resultType: string, searchQuery?: string }} params
 */
export async function sendGa4SearchResultIswClickEvent({ resultType, searchQuery }) {
  await sendGa4EventToLambda(GA4_BOOST_EVENT.SEARCH_RESULT_ISW_CLICK, {
    result_type: resultType || '',
    qs: searchQuery || '',
  });
}

/**
 * `boost_notify_me`
 *
 * Fires when a user subscribes to back-in-stock notifications.
 *
 * @param {{ productId: string | number, searchQuery?: string, widgetId?: string }} params
 */
export async function sendGa4NotifyMeEvent({ productId, searchQuery, widgetId }) {
  await sendGa4EventToLambda(GA4_BOOST_EVENT.NOTIFY_ME, {
    pid: String(productId || ''),
    qs: searchQuery || '',
    wid: widgetId || '',
  });
}

/**
 * `boost_add_to_cart`
 *
 * Fires when a user adds a product to cart from a widget that bypasses the
 * standard `onClickTrackingEvent` click flow (e.g. cart-drawer RCU "Add"
 * button, tiered-bundle Add / variant-dropdown item).
 *
 * Callers dispatch a custom window event; `initAnalytic()` listens and
 * calls this function to keep GA4 logic centralised in the analytic module.
 *
 * @param {{ productId: string | number, variantId?: string | number, widgetId?: string, source?: string }} params
 */
export async function sendGa4AddToCartEvent({ productId, variantId, widgetId, source }) {
  await sendGa4EventToLambda(GA4_BOOST_EVENT.ADD_TO_CART, {
    pid: String(productId || ''),
    vid: String(variantId || ''),
    wid: widgetId || '',
    source: source || '',
  });
}

// ─── Widget view tracking (IntersectionObserver) ─────────────────────────────

/** Prevents setting up duplicate observers on the same element. */
const _observedWidgets = new WeakSet();

/** Fires `boost_widget_view` once when element enters the viewport. @param {Element} element */
function observeWidgetElement(element) {
  if (_observedWidgets.has(element)) return;
  _observedWidgets.add(element);

  const widgetId = /** @type {HTMLElement} */ (element).dataset?.widgetId || element.id || '';
  // Dynamic bundles use class "boost-sd__recommendation--dynamic" (not "--bundle"),
  // but their widgetId always starts with "bundle-". Check both.
  const source =
    element.classList.contains('boost-sd__recommendation--bundle') || widgetId.startsWith('bundle')
      ? 'bundle'
      : 'recommendation';

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sendGa4EventToLambda(GA4_BOOST_EVENT.WIDGET_VIEW, { wid: widgetId, source });
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0 }
  );

  observer.observe(element);
}

/**
 * Sets up `boost_widget_view` tracking for recommendation/bundle widgets.
 * Observes existing elements immediately and watches for dynamic additions via MutationObserver.
 * Call once from `initAnalytic()`.
 */
export function initGa4WidgetViewTracking() {
  // Selectors for widgets that should fire boost_widget_view:
  // - .boost-sd__recommendation  : recommendation / dynamic-bundle widgets
  // - .boost-sd__tier-discount--bundle : tiered-bundle widgets (product page
  //   via TAE block, search page via tier-discount-search.helper). Their
  //   data-widget-id starts with "bundle-" so source resolves to 'bundle'.
  const WIDGET_SELECTOR = '.boost-sd__recommendation, .boost-sd__tier-discount--bundle';

  document.querySelectorAll(WIDGET_SELECTOR).forEach(observeWidgetElement);

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(WIDGET_SELECTOR)) observeWidgetElement(node);
        node.querySelectorAll(WIDGET_SELECTOR).forEach(observeWidgetElement);
      }
    }
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

/**
 * Clears the sent-query deduplication set.
 * Useful in tests or when the shop GA4 configuration changes at runtime.
 */
export function resetGa4TrackingCache() {
  _sentSearchQueries.clear();
}
